import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

interface MediaContextType {
  mediaUrl: string | null;
  mediaType: string | null;
  mediaTitle: string | null;
  isVisible: boolean;
  openMedia: (url: string, type: string, title: string) => void;
  closeMedia: () => void;
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

export const useMediaContext = (): MediaContextType => {
  const context = useContext(MediaContext);
  if (!context) {
    throw new Error('useMediaContext must be used within a MediaProvider');
  }
  return context;
};

interface MediaProviderProps {
  children: ReactNode;
}

export const MediaProvider: React.FC<MediaProviderProps> = ({ children }) => {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [mediaTitle, setMediaTitle] = useState<string | null>(null);

  const openMedia = useCallback(
    (url: string, type: string, title: string) => {
      setMediaUrl(url);
      setMediaType(type);
      setIsVisible(true);
      setMediaTitle(title);
    },
    [],
  );

  const closeMedia = useCallback(() => {
    setMediaUrl(null);
    setMediaType(null);
    setMediaTitle(null);
    setIsVisible(false);
  }, []);

  return (
    <MediaContext.Provider
      value={{
        mediaUrl,
        mediaType,
        mediaTitle,
        isVisible,
        openMedia,
        closeMedia,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
};
